import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, consumeGuestReturnTo } from '../contexts/AuthContext';
import { CoArchitectLogo, AlertCircle, GoogleIcon } from '../components/Icons';
import { API } from '../contexts/AuthContext';
import { mixpanel } from '../lib/mixpanel';

const GOOGLE_AUTH_URL = `${API}/auth/google`;

const COUNTRY_CODES: { code: string; label: string }[] = [
  { code: '91',  label: '+91  India' },
  { code: '54',  label: '+54  Argentina' },
  { code: '61',  label: '+61  Australia' },
  { code: '880', label: '+880  Bangladesh' },
  { code: '32',  label: '+32  Belgium' },
  { code: '55',  label: '+55  Brazil' },
  { code: '1',   label: '+1  Canada' },
  { code: '86',  label: '+86  China' },
  { code: '45',  label: '+45  Denmark' },
  { code: '33',  label: '+33  France' },
  { code: '49',  label: '+49  Germany' },
  { code: '233', label: '+233  Ghana' },
  { code: '62',  label: '+62  Indonesia' },
  { code: '353', label: '+353  Ireland' },
  { code: '39',  label: '+39  Italy' },
  { code: '81',  label: '+81  Japan' },
  { code: '254', label: '+254  Kenya' },
  { code: '60',  label: '+60  Malaysia' },
  { code: '52',  label: '+52  Mexico' },
  { code: '31',  label: '+31  Netherlands' },
  { code: '64',  label: '+64  New Zealand' },
  { code: '234', label: '+234  Nigeria' },
  { code: '47',  label: '+47  Norway' },
  { code: '92',  label: '+92  Pakistan' },
  { code: '63',  label: '+63  Philippines' },
  { code: '48',  label: '+48  Poland' },
  { code: '351', label: '+351  Portugal' },
  { code: '966', label: '+966  Saudi Arabia' },
  { code: '65',  label: '+65  Singapore' },
  { code: '27',  label: '+27  South Africa' },
  { code: '82',  label: '+82  South Korea' },
  { code: '34',  label: '+34  Spain' },
  { code: '94',  label: '+94  Sri Lanka' },
  { code: '46',  label: '+46  Sweden' },
  { code: '41',  label: '+41  Switzerland' },
  { code: '66',  label: '+66  Thailand' },
  { code: '971', label: '+971  UAE' },
  { code: '44',  label: '+44  United Kingdom' },
  { code: '1',   label: '+1  United States' },
  { code: '977', label: '+977  Nepal' },
];

type Step = 'form' | 'verify';

function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { sendEmailVerification, verifyEmailOTP, register, user } = useAuth();

  // A failed guest-session attempt (e.g. Landing.tsx's startGuestFlow hitting the daily
  // per-IP cap) redirects here with the server's message riding along in nav state.
  const guestError = (location.state as { guestError?: string } | null)?.guestError;

  // ── Step 1: signup form ───────────────────────────────────────────────────
  const [step, setStep]                   = useState<Step>('form');
  const [name, setName]                   = useState('');
  const [city, setCity]                   = useState('');
  const [mobile, setMobile]               = useState('');
  const [countryCode, setCountryCode]     = useState('91');
  const [email, setEmail]                 = useState('');
  const [password, setPassword]           = useState('');
  const [showPass, setShowPass]           = useState(false);
  const [formError, setFormError]         = useState(guestError ?? '');
  const [formErrorIsExisting, setFormErrorIsExisting] = useState(false);
  const [formLoading, setFormLoading]     = useState(false);

  // ── Step 2: OTP verification ──────────────────────────────────────────────
  const [otp, setOtp]                     = useState(['', '', '', '', '', '']);
  const [verificationToken, setVToken]    = useState('');
  const [otpError, setOtpError]           = useState('');
  const [otpErrorIsExisting, setOtpErrorIsExisting] = useState(false);
  const [otpLoading, setOtpLoading]       = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpRefs                           = useRef<(HTMLInputElement | null)[]>([]);

  const handleBack = () => {
    if (step === 'verify') setStep('form');
    else navigate('/login');
  };

  const isMobileValid = mobile === '' || /^\d{5,12}$/.test(mobile);
  const canSubmitForm = name.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && password.length >= 8 && isMobileValid;
  const otpFilled     = otp.every(d => d !== '');

  // Resend cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown(v => v - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  // ── Step 1 submit ─────────────────────────────────────────────────────────
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFormErrorIsExisting(false);
    setFormLoading(true);
    try {
      await sendEmailVerification(email.trim().toLowerCase());
      mixpanel.track('signup_started', { method: 'email' });
      setStep('verify');
      setResendCooldown(30);
    } catch (err: any) {
      if ((err as any).response?.status === 409) {
        setFormError('An account with this email already exists.');
        setFormErrorIsExisting(true);
      } else {
        setFormError('Could not send verification email. Please try again.');
      }
    } finally {
      setFormLoading(false);
    }
  };

  // ── OTP input handling ─────────────────────────────────────────────────────
  const handleOtpChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next  = [...otp];
    next[index] = digit;
    setOtp(next);
    if (otpError) { setOtpError(''); setOtpErrorIsExisting(false); }
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length === 6) {
      setOtp(text.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  // ── Step 2: verify OTP then register ─────────────────────────────────────
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError('');
    setOtpErrorIsExisting(false);
    setOtpLoading(true);
    try {
      let vToken = verificationToken;
      if (!vToken) {
        try {
          vToken = await verifyEmailOTP(email.trim().toLowerCase(), otp.join(''));
          setVToken(vToken);
        } catch (verifyErr: any) {
          const msg: string = verifyErr.response?.data?.error ?? '';
          if (verifyErr.response?.status === 410 || msg.toLowerCase().includes('expired')) {
            setOtpError('This code has expired — tap Resend to get a new one.');
          } else {
            setOtpError('That code doesn\'t match. Double-check your email and try again.');
          }
          return;
        }
      }

      try {
        const newUser = await register(email.trim().toLowerCase(), password, name.trim(), city.trim(), vToken, mobile ? '+' + countryCode + mobile : undefined);
        navigate(consumeGuestReturnTo(newUser.id), { replace: true });
      } catch (regErr: any) {
        // Registration failed (e.g. 409 on an already-registered email) — this session never
        // became the guest's claimant, so drop any stale guest-return keys rather than let them
        // leak into whatever auth flow happens next.
        consumeGuestReturnTo();
        if (regErr.response?.status === 409) {
          setOtpError('An account with this email already exists.');
          setOtpErrorIsExisting(true);
        } else {
          setOtpError('Something went wrong creating your account. Please try again.');
        }
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setOtpError('');
    setOtpErrorIsExisting(false);
    setVToken('');
    try {
      await sendEmailVerification(email.trim().toLowerCase());
      setOtp(['', '', '', '', '', '']);
      setResendCooldown(30);
      otpRefs.current[0]?.focus();
    } catch {
      setOtpError('Could not resend. Please try again.');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="auth-page">
      <div style={{ width: '100%', maxWidth: 420 }}>
        <button className="auth-back-btn" onClick={handleBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          {step === 'verify' ? 'Back' : 'Back to Login'}
        </button>
      </div>

      <div className="auth-card">
        <div className="auth-brand">
          <div className="auth-brand-logo">
            <CoArchitectLogo size={80} />
          </div>
          <h1>Create Account</h1>
          <p>{step === 'verify' ? `Enter the code sent to ${email}` : 'Join Co-Architect today'}</p>
        </div>

        {step === 'form' ? (
          <>
            {/* Google Sign-Up — primary */}
            <a
              href={user?.role === 'guest' ? `${GOOGLE_AUTH_URL}?guest=${encodeURIComponent(localStorage.getItem('accessToken') ?? '')}` : GOOGLE_AUTH_URL}
              className="btn-google"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                       height: 46, width: '100%', textDecoration: 'none', marginBottom: 4 }}
            >
              <GoogleIcon size={18} />
              Continue with Google
            </a>

            <div className="auth-divider"><span>or sign up with email</span></div>

            {/* Email signup form */}
            <form onSubmit={handleFormSubmit} className="auth-form" noValidate>
              {formError && (
                <div className="alert alert-error" role="alert">
                  <AlertCircle size={15} style={{ flexShrink: 0 }} />
                  <span>
                    {formError}{' '}
                    {formErrorIsExisting && (
                      <Link to="/login" className="auth-link" style={{ fontSize: 'inherit' }}>Sign in instead</Link>
                    )}
                  </span>
                </div>
              )}

              <div className="form-field">
                <label className="form-label" htmlFor="name">
                  Full Name<span className="required">*</span>
                </label>
                <input
                  id="name"
                  className="form-input"
                  type="text"
                  autoComplete="name"
                  placeholder="Your full name"
                  value={name}
                  onChange={e => { setName(e.target.value); if (formError) setFormError(''); }}
                  autoFocus
                  required
                />
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="city">
                  City <span className="caption" style={{ fontWeight: 400 }}>(optional)</span>
                </label>
                <input
                  id="city"
                  className="form-input"
                  type="text"
                  autoComplete="address-level2"
                  placeholder="City you're based in"
                  value={city}
                  onChange={e => { setCity(e.target.value); if (formError) setFormError(''); }}
                />
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="signup-mobile">
                  Phone Number <span className="caption" style={{ fontWeight: 400 }}>(optional)</span>
                </label>
                <div className={`phone-input-group${!isMobileValid ? ' error' : ''}`}>
                  <select
                    className="phone-cc-select"
                    value={countryCode}
                    onChange={e => setCountryCode(e.target.value)}
                    aria-label="Country code"
                  >
                    {COUNTRY_CODES.map(c => (
                      <option value={c.code} key={c.code + c.label}>{c.label}</option>
                    ))}
                  </select>
                  <input
                    id="signup-mobile"
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="tel"
                    placeholder="Phone number"
                    value={mobile}
                    onChange={e => { setMobile(e.target.value.replace(/\D/g, '').slice(0, 12)); if (formError) setFormError(''); }}
                  />
                </div>
                {!isMobileValid && (
                  <span className="form-error" role="alert">
                    <AlertCircle size={13} />Enter a valid phone number
                  </span>
                )}
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="signup-email">
                  Email<span className="required">*</span>
                </label>
                <input
                  id="signup-email"
                  className="form-input"
                  type="email"
                  autoComplete="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={e => { setEmail(e.target.value); if (formError) { setFormError(''); setFormErrorIsExisting(false); } }}
                  required
                />
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="signup-password">
                  Password<span className="required">*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="signup-password"
                    className="form-input"
                    type={showPass ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Min. 8 characters"
                    value={password}
                    onChange={e => { setPassword(e.target.value); if (formError) setFormError(''); }}
                    style={{ paddingRight: 40 }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(v => !v)}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                             background: 'none', border: 'none', cursor: 'pointer',
                             color: 'var(--text-faint)', fontSize: 12, padding: 4 }}
                    aria-label={showPass ? 'Hide password' : 'Show password'}
                  >
                    {showPass ? 'Hide' : 'Show'}
                  </button>
                </div>
                <p className="form-helper">Minimum 8 characters.</p>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={formLoading || !canSubmitForm}
              >
                {formLoading
                  ? <><span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Sending code…</>
                  : 'Continue'}
              </button>
            </form>
          </>
        ) : (
          /* ── Step 2: OTP verification ─────────────────────────────────────── */
          <form onSubmit={handleVerify} className="auth-form" noValidate>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-faint)', textAlign: 'center', marginBottom: 8 }}>
              Check your inbox and enter the 6-digit code.
            </p>

            <div className="otp-container" onPaste={handleOtpPaste}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={el => { otpRefs.current[i] = el; }}
                  className={`otp-input${otpError ? ' error' : ''}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  autoFocus={i === 0}
                  autoComplete="one-time-code"
                />
              ))}
            </div>

            {otpError && (
              <span className="form-error" role="alert">
                <AlertCircle size={13} />
                <span>
                  {otpError}{' '}
                  {otpErrorIsExisting && (
                    <Link to="/login" className="auth-link" style={{ fontSize: 'inherit' }}>Sign in instead</Link>
                  )}
                </span>
              </span>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={otpLoading || !otpFilled}
            >
              {otpLoading
                ? <><span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Creating account…</>
                : 'Verify & Create Account'}
            </button>

            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleResend}
              disabled={resendCooldown > 0}
              style={{ fontSize: '0.8125rem' }}
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
            </button>
          </form>
        )}

        <div className="auth-footer">
          Already have an account?{' '}
          <Link to="/login" className="auth-link">Sign in</Link>
        </div>
      </div>

      {/* Social + legal footer */}
      <div className="auth-social-footer">
        <a href="https://www.instagram.com/coarchitect.ai" target="_blank" rel="noopener noreferrer" className="auth-social-row">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="5"/>
            <circle cx="12" cy="12" r="4"/>
            <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
          </svg>
          @coarchitect.ai
        </a>
        <div className="auth-legal-row">
          <Link to="/privacy" className="auth-legal-link">Privacy Policy</Link>
          <span className="auth-legal-sep" />
          <Link to="/terms" className="auth-legal-link">Terms of Service</Link>
        </div>
      </div>
    </div>
  );
}

export default Signup;
